import type { ScoreBreakdown } from '../game/types.ts'
import block1 from '../assets/block1.webp'
import block2 from '../assets/block2.webp'
import { BONUS_PERCENT } from '../config/scoring.ts'
import { t, tx } from '../i18n/translations.ts'

interface Props {
  blackScore: ScoreBreakdown
  whiteScore: ScoreBreakdown
  turn: 'black' | 'white'
  playerColor: 'black' | 'white'
  highscore: number
}

function LineBreakdown({ lineCounts }: { lineCounts: Record<number, number> }) {
  const entries = Object.entries(lineCounts)
    .map(([k, v]) => ({ len: Number(k), count: v }))
    .sort((a, b) => b.len - a.len)

  if (entries.length === 0) return <span className="score-hint">{t('scoreHintPlaceStone')}</span>

  return (
    <span className="line-breakdown">
      {entries.map(({ len, count }) => (
        <span key={len} className={`line-tag line-tag--${len}`}>
          {tx('lineCount', { len, count })}
        </span>
      ))}
    </span>
  )
}

// 駒の色は固定（黒=block1 青系 / 白=block2 赤系）。ラベル（あなた/CPU）は playerColor で動的に切替。
function iconForColor(color: 'black' | 'white'): string {
  return color === 'black' ? block1 : block2
}

export function ScorePanel({
  blackScore,
  whiteScore,
  turn,
  playerColor,
  highscore,
}: Props) {
  const cpuColor: 'black' | 'white' = playerColor === 'black' ? 'white' : 'black'
  const playerScore = playerColor === 'black' ? blackScore : whiteScore
  const cpuScore = playerColor === 'black' ? whiteScore : blackScore
  // 後手（= white）にはスコア +10% バッジを表示
  const playerHasKomi = playerColor === 'white'
  const cpuHasKomi = cpuColor === 'white'

  return (
    <div className="score-panel">
      {/* プレイヤー（左固定） */}
      <div className={`score-card ${turn === playerColor ? 'score-card--active' : ''}`}>
        <div className="score-card__header">
          <img src={iconForColor(playerColor)} alt={t('youConstant')} className="score-card__icon" />
          <span className="score-card__name">{t('youConstant')}</span>
          {playerHasKomi && (
            <span className="score-card__komi">+{BONUS_PERCENT}%</span>
          )}
          {turn === playerColor && <span className="score-card__turn">▶</span>}
        </div>
        <div className="score-card__total">
          {Math.round(playerScore.total).toLocaleString()}
          <span className="score-card__unit">pt</span>
        </div>
        <div className="score-card__detail">
          <LineBreakdown lineCounts={playerScore.lineCounts} />
          {playerScore.clusterBonus > 0 && (
            <span className="cluster-bonus">+{Math.round(playerScore.clusterBonus)}{t('clusterUnit')}</span>
          )}
        </div>
        {highscore > 0 && (
          <div className="score-card__best">{t('best')}: {highscore.toLocaleString()}</div>
        )}
      </div>

      <div className="score-panel__vs">VS</div>

      {/* CPU（右固定） */}
      <div className={`score-card ${turn === cpuColor ? 'score-card--active' : ''}`}>
        <div className="score-card__header">
          <img src={iconForColor(cpuColor)} alt="CPU" className="score-card__icon" />
          <span className="score-card__name">CPU</span>
          {cpuHasKomi && (
            <span className="score-card__komi">+{BONUS_PERCENT}%</span>
          )}
          {turn === cpuColor && <span className="score-card__turn">▶</span>}
        </div>
        <div className="score-card__total">
          {Math.round(cpuScore.total).toLocaleString()}
          <span className="score-card__unit">pt</span>
        </div>
        <div className="score-card__detail">
          <LineBreakdown lineCounts={cpuScore.lineCounts} />
          {cpuScore.clusterBonus > 0 && (
            <span className="cluster-bonus">+{Math.round(cpuScore.clusterBonus)}{t('clusterUnit')}</span>
          )}
        </div>
      </div>
    </div>
  )
}
